import { Link } from "react-router-dom";

import { PublicShell } from "../components/PublicShell";
import { usePrice, useSubscriptionPriceText } from "../hooks/usePrice";
import { formatRupees } from "../utils/money";
import { useT } from "../i18n/LanguageProvider";
import { useIsMobile } from "../hooks/useIsMobile";
import { C } from "../styles/tokens";

// =============================================================================
// Landing -- ID.me-inspired layout (client revision 10-07-2026):
//   hero (centered copy + interview photo) -> "special offers coming soon"
//   community grid -> navy "get started" band -> community benefit tiles ->
//   navy impact stats -> bottom CTA -> trust line.
// "For businesses" / "For academic institutions" content lives on its own
// pages now (/business, /institutions), linked from the header corner.
// =============================================================================

export function Landing() {
  const t = useT();
  const subscriptionText = useSubscriptionPriceText();
  const institutionPrice = usePrice("INSTITUTION_SUBSCRIPTION").pricePaise;
  const organisationPrice = usePrice("ORGANISATION_SUBSCRIPTION").pricePaise;
  const isMobile = useIsMobile();
  return (
    <PublicShell>
      {/* -- Hero ------------------------------------------------------- */}
      <section
        style={{
          background: `linear-gradient(180deg, ${C.surface} 0%, #fff 100%)`,
          padding: isMobile ? "36px 20px 44px" : "64px 40px 95px",
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1.05fr 1fr",
            gap: isMobile ? 32 : 48,
            alignItems: "center",
          }}
        >
          {/* Left column -- stacked headline, centre-aligned within the column */}
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                fontSize: isMobile ? 38 : 52,
                fontWeight: 800,
                lineHeight: 1.08,
                margin: "0 0 20px",
                letterSpacing: -1.2,
              }}
            >
              <span style={{ display: "block", color: C.navy }}>
                {t("landing.heroLine1")}
              </span>
              <span style={{ display: "block", color: C.navy }}>
                {t("landing.heroLine2")}
              </span>
              <span style={{ display: "block", color: C.primary }}>
                {t("landing.heroLine3")}
              </span>
            </h1>
            <p
              style={{
                fontSize: 17,
                color: C.textMed,
                lineHeight: 1.55,
                margin: "0 auto 28px",
                maxWidth: 480,
                fontWeight: 500,
              }}
            >
              {t("landing.tagline")}
            </p>
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Link
                to="/register"
                style={{
                  padding: "12px 26px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "white",
                  background: C.primary,
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                {t("landing.heroLine3")}
              </Link>
              <Link
                to="/login"
                style={{
                  padding: "12px 26px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: C.navy,
                  background: "transparent",
                  border: `1.5px solid ${C.navy}`,
                  borderRadius: 6,
                  textDecoration: "none",
                }}
              >
                {t("common.signIn")}
              </Link>
            </div>
            {subscriptionText && (
              <div
                style={{
                  marginTop: 16,
                  fontSize: 12.5,
                  color: "#64748b",
                  textAlign: "center",
                }}
              >
                Subscription: {subscriptionText}
              </div>
            )}
          </div>

          {/* Right column -- interview photo */}
          <img
            src="/hero-interview.jpg"
            alt="An ongoing interview between a candidate and a hiring manager"
            style={{
              width: "100%",
              borderRadius: 16,
              boxShadow: "0 24px 60px -24px rgba(22,41,76,0.45)",
              objectFit: "cover",
              aspectRatio: "3 / 2",
            }}
          />
        </div>
      </section>

      {/* -- "Special offers coming for you soon!" community grid ------- */}
      <section
        style={{
          padding: isMobile ? "44px 20px" : "64px 40px",
          maxWidth: 1120,
          margin: "0 auto",
        }}
      >
        <h2 style={sectionTitle(isMobile)}>{t("landing.offersTitle")}</h2>
        <p style={sectionSubtitle}>{t("landing.offersSubtitle")}</p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 160 : 247}px, 1fr))`,
            gap: 14,
          }}
        >
          {/* Alphabetical order and a single blue pill colour -- 24-07 client feedback. */}
          <OfferTile
            img="/offers/defence-v2.jpg"
            label={t("landing.offDefence")}
            accent={PILL_BLUE}
          />
          <OfferTile
            img="/offers/doctors-v2.jpg"
            label={t("landing.offMedical")}
            accent={PILL_BLUE}
          />
          <OfferTile
            img="/offers/responders-v2.jpg"
            label={t("landing.offResponders")}
            accent={PILL_BLUE}
          />
          <OfferTile
            img="/offers/government-v2.jpg"
            label={t("landing.offGovt")}
            accent={PILL_BLUE}
          />
          <OfferTile
            img="/offers/nurses-v2.jpg"
            label={t("landing.offNurses")}
            accent={PILL_BLUE}
          />
          <OfferTile
            img="/offers/police-v2.jpg"
            label={t("landing.offPolice")}
            accent={PILL_BLUE}
          />
          <OfferTile
            img="/offers/students-v2.jpg"
            label={t("landing.offStudents")}
            accent={PILL_BLUE}
          />
          <OfferTile
            img="/offers/teachers-v2.jpg"
            label={t("landing.offTeachers")}
            accent={PILL_BLUE}
          />
        </div>
      </section>

      {/* -- Talent Hub Green Banner ------------------------------------------ */}
      <section
        style={{
          padding: isMobile ? "24px 20px 48px" : "32px 40px 64px",
          maxWidth: 1080,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(135deg, #295ebd 0%, rgb(5, 92, 43) 100%)",
            borderRadius: 24,
            padding: isMobile ? "40px 24px" : "56px 48px",
            color: "#ffffff",
            boxShadow: "#0b0c0e 0px 20px 50px -10px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Subtle decorative glow effect */}
          <div
            style={{
              position: "absolute",
              top: "-40%",
              right: "-10%",
              width: "400px",
              height: "400px",
              background:
                "radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)",
              borderRadius: "50%",
              pointerEvents: "none",
            }}
          />

          <div style={{ maxWidth: 680, position: "relative", zIndex: 2 }}>
            {/* Badge */}
            <div
              style={{
                display: "inline-block",
                padding: "6px 14px",
                background: "rgba(255, 255, 255, 0.18)",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: 1.5,
                marginBottom: 16,
                color: "#ffffff",
                backdropFilter: "blur(4px)",
              }}
            >
              TALENT HUB
            </div>

            {/* Headline */}
            <h2
              style={{
                fontSize: isMobile ? 28 : 38,
                fontWeight: 800,
                lineHeight: 1.15,
                margin: "0 0 16px",
                letterSpacing: -0.5,
                color: "#ffffff",
              }}
            >
              Your next opportunity starts here.
            </h2>

            {/* Description */}
            <p
              style={{
                fontSize: isMobile ? 15 : 16.5,
                lineHeight: 1.6,
                margin: "0 0 28px",
                color: "rgba(255, 255, 255, 0.92)",
                fontWeight: 400,
              }}
            >
              Discover the right opportunities. Get noticed for what you've
              truly earned. Find jobs, connect with employers, and move forward
              with verified credentials you can trust.
            </p>

            {/* CTA Button */}
            <div style={{ marginBottom: 24 }}>
              <Link
                to="/gateway"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "14px 30px",
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#ffffff",
                  background: "#182B49", // Dark Navy Button for high contrast
                  borderRadius: 10,
                  textDecoration: "none",
                  boxShadow: "0 8px 20px rgba(0, 0, 0, 0.25)",
                  transition:
                    "transform 0.18s ease, background-color 0.18s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.border = "2px solid rgb(8, 131, 62)";
                  e.currentTarget.style.boxShadow =
                    "0 0 20px rgba(8, 131, 62, 0.40)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.border =
                    "1px solid rgba(0, 0, 0, 0.08)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 30px rgba(0, 0, 0, 0.06)";
                }}
              >
                Explore Opportunities
                <ArrowIcon />
              </Link>
            </div>

            {/* Sub-footer line */}
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span>Trusted credentials</span>
              <span aria-hidden="true">|</span>
              <span>Better opportunities</span>
              <span aria-hidden="true">|</span>
              <span>Meaningful connections</span>
            </div>
          </div>
        </div>
      </section>

      {/* -- Navy "get started" band -- three signup cards --------------- */}
      <section
        style={{
          background: C.navy,
          padding: isMobile ? "44px 20px" : "64px 40px",
        }}
      >
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <h2
            style={{
              margin: "0 0 8px",
              textAlign: "center",
              fontSize: isMobile ? 27 : 34,
              fontWeight: 800,
              color: "#fff",
              letterSpacing: -0.5,
            }}
          >
            {t("landing.joinTitle")}
          </h2>
          <p
            style={{
              margin: "0 0 36px",
              textAlign: "center",
              fontSize: 16,
              color: C.onNavyMuted,
              lineHeight: 1.5,
            }}
          >
            {t("landing.joinSubtitle")}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 16,
            }}
          >
            <SignupCard
              title={t("landing.ctaProfessional")}
              description={t("landing.ctaProfessionalDesc")}
              to="/register"
              cta={t("landing.ctaProfessionalBtn")}
              accent={C.primary}
            />
            <SignupCard
              priceLabel={
                institutionPrice &&
                t("landing.ctaInstitutionPrice", {
                  price: formatRupees(institutionPrice),
                })
              }
              title={t("landing.ctaInstitution")}
              description={t("landing.ctaInstitutionDesc")}
              to="/register/institution"
              cta={t("landing.ctaInstitutionBtn")}
              accent={C.navy}
            />
            <SignupCard
              priceLabel={
                organisationPrice &&
                t("landing.ctaOrgPrice", {
                  price: formatRupees(organisationPrice),
                })
              }
              title={t("landing.ctaOrg")}
              description={t("landing.ctaOrgDesc")}
              to="/register/organisation"
              cta={t("landing.ctaOrgBtn")}
              accent={C.amber}
            />
          </div>
        </div>
      </section>

      {/* -- Community benefit tiles ------------------------------------ */}
      <section
        style={{
          padding: isMobile ? "44px 20px" : "72px 40px",
          maxWidth: 1080,
          margin: "0 auto",
        }}
      >
        <h2 style={sectionTitle(isMobile)}>{t("landing.communityTitle")}</h2>
        <p style={sectionSubtitle}>{t("landing.communitySubtitle")}</p>
        <div
          data-responsive-grid="3"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 16,
          }}
        >
          <CommunityTile
            img="/tiles/professionals-v3.jpg"
            label={t("landing.commProfessionals")}
            description={t("landing.commProfessionalsDesc")}
            accent={PILL_BLUE}
          />
          <CommunityTile
            img="/tiles/employers.jpg"
            label={t("landing.commEmployers")}
            description={t("landing.commEmployersDesc")}
            accent={PILL_BLUE}
          />
          <CommunityTile
            img="/tiles/institutions.jpg"
            label={t("landing.commInstitutions")}
            description={t("landing.commInstitutionsDesc")}
            accent={PILL_BLUE}
          />
        </div>
      </section>

      {/* -- Impact stats -- navy band ----------------------------------- */}
      <section
        style={{
          background: C.navy,
          padding: isMobile ? "44px 20px" : "64px 40px",
        }}
      >
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <h2
            style={{
              margin: "0 0 32px",
              textAlign: "center",
              fontSize: isMobile ? 27 : 34,
              fontWeight: 800,
              color: "#fff",
              letterSpacing: -0.5,
            }}
          >
            {t("landing.impactTitle")}
          </h2>
          <div
            data-responsive-grid="stats"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 16,
            }}
          >
            <StatCard
              count={t("landing.impactMembersCount")}
              label={t("landing.impactMembersLabel")}
              description={t("landing.impactMembersDesc")}
            />
            <StatCard
              count={t("landing.impactOrgsCount")}
              label={t("landing.impactOrgsLabel")}
              description={t("landing.impactOrgsDesc")}
            />
            <StatCard
              count={t("landing.impactInstCount")}
              label={t("landing.impactInstLabel")}
              description={t("landing.impactInstDesc")}
            />
          </div>
        </div>
      </section>

      {/* -- Bottom CTA ------------------------------------------------- */}
      <section
        style={{
          padding: isMobile ? "48px 20px" : "64px 40px",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            margin: "0 auto 24px",
            maxWidth: 680,
            fontSize: isMobile ? 24 : 30,
            fontWeight: 800,
            color: C.navy,
            letterSpacing: -0.4,
            lineHeight: 1.25,
          }}
        >
          {t("landing.bottomCtaTitle")}
        </h2>
        <Link
          to="/register"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "13px 28px",
            fontSize: 15,
            fontWeight: 700,
            color: "#fff",
            background: C.primary,
            borderRadius: 6,
            textDecoration: "none",
          }}
        >
          {t("landing.bottomCtaBtn")}
          <ArrowIcon />
        </Link>
      </section>

      {/* -- Trust signals ---------------------------------------------- */}
      <section
        style={{
          padding: "28px 32px",
          textAlign: "center",
          background: C.surface,
          borderTop: `1px solid ${C.divider}`,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: C.textLight,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            marginBottom: 8,
          }}
        >
          Built for trust
        </div>
        <div
          style={{
            fontSize: 13,
            color: C.textMed,
            maxWidth: 640,
            margin: "0 auto",
          }}
        >
          Every credential on TickAndPass is verified by the institution or
          employer that issued it.
        </div>
      </section>
    </PublicShell>
  );
}

// Single pill colour for the benefits grid (brand logo blue) -- the client
// found the multicoloured pills out of place.
// Change from:
// const PILL_BLUE = "#1565D8";

// To:
const PILL_BLUE = "rgb(8, 131, 62)";

// ---- shared text styles ----

const sectionTitle = (isMobile) => ({
  margin: "0 0 8px",
  textAlign: "center",
  fontSize: isMobile ? 27 : 34,
  fontWeight: 800,
  color: C.navy,
  letterSpacing: -0.5,
});

const sectionSubtitle = {
  margin: "0 0 32px",
  textAlign: "center",
  fontSize: 16,
  color: C.textMed,
  lineHeight: 1.5,
};

// ---- subcomponents ----

/** Community photo tile in the "special offers coming soon" grid -> coming-soon page. */
/**  function OfferTile({ img, label, accent }) {
  return (
    <Link
      to={`/coming-soon?for=${encodeURIComponent(label)}`}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: 0,
        background: "#fff",
        border: `1px solid ${C.divider}`,
        borderRadius: 12,
        overflow: "hidden",
        textDecoration: "none",
        transition: "box-shadow 0.18s ease, transform 0.18s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 10px 24px rgba(22,41,76,0.14)";
        e.currentTarget.style.transform = "translateY(-3px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div style={{ width: "100%", position: "relative" }}>
        <img
          src={img}
          alt={label}
          loading="lazy"
          style={{ width: "100%", height: 130, objectFit: "cover", objectPosition: "center 30%", display: "block" }}
        />
        <span style={{
          position: "absolute", bottom: -12, left: "50%", transform: "translateX(-50%)",
          maxWidth: "calc(100% - 16px)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          background: accent, color: "#fff",
          padding: "5px 14px", borderRadius: 999,
          fontSize: 11.5, fontWeight: 700,
          boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
        }}>
          {label}
        </span>
      </div>
      <div style={{
        padding: "22px 10px 14px",
        fontSize: 11, fontWeight: 700, color: C.textLight,
        textTransform: "uppercase", letterSpacing: 0.5,
      }}>
        Coming soon
      </div>
    </Link>
  );
}*/

/** Community photo tile in the "special offers coming soon" grid -> coming-soon page.
 * New style for the Professionals tiles
 */
function OfferTile({ img, label, accent }) {
  return (
    <Link
      to={`/coming-soon?for=${encodeURIComponent(label)}`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "#fff",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        borderRadius: 24, // Matches the smooth rounded corners in the image
        overflow: "hidden",
        textDecoration: "none",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.06)", // Soft card shadow
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 16px 36px rgba(0, 0, 0, 0.12)";
        e.currentTarget.style.transform = "translateY(-4px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.06)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Top Image Section */}
      <div style={{ width: "100%", position: "relative" }}>
        <img
          src={img}
          alt={label}
          loading="lazy"
          style={{
            width: "100%",
            height: 210, // Taller image proportions matching the design
            objectFit: "cover",
            objectPosition: "center 30%",
            display: "block",
          }}
        />

        {/* Center Green Badge Pill (Fits within card boundaries) */}
        <span
          title={label}
          style={{
            position: "absolute",
            bottom: -20,
            left: "50%",
            transform: "translateX(-50%)",
            maxWidth: "calc(100% - 24px)", // Prevents badge from extending outside card
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            background: accent || "rgb(8, 131, 62)",
            color: "#ffffff",
            padding: "8px 16px", // Adjusted padding for long text
            borderRadius: 999,
            fontSize: 13.5, // Optimal size for long text labels
            fontWeight: 700,
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
          }}
        >
          {label}
        </span>
      </div>

      {/* Bottom White Section */}
      <div
        style={{
          padding: "48px 16px 36px",
          fontSize: 13,
          fontWeight: 700,
          color: "#7E8799",
          textTransform: "uppercase",
          letterSpacing: 2.5,
          textAlign: "center",
        }}
      >
        Coming soon
      </div>
    </Link>
  );
}

/**function SignupCard({ priceLabel, title, description, to, cta, accent }) {
  return (
    <Link
      to={to}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        textAlign: "center",
        padding: 24,
        background: "#fff",
        borderRadius: 8,
        textDecoration: "none", color: C.textDark,
        transition: "box-shadow 0.18s ease, transform 0.18s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 10px 26px rgba(0,0,0,0.28)";
        e.currentTarget.style.transform = "translateY(-3px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div style={{
        display: "inline-block", padding: "4px 12px",
        background: accent === C.navy ? "#E9EEF6" : accent === C.amber ? C.amberLight : C.primaryLight,
        color: accent,
        borderRadius: 999,
        fontSize: 12, fontWeight: 800,
        textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 14,
      }}>
        {priceLabel}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ fontSize: 14, color: C.textMed, lineHeight: 1.5, marginBottom: 18, flex: 1 }}>
        {description}
      </div>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "11px 20px",
        background: accent, color: "white",
        fontSize: 14, fontWeight: 700,
        borderRadius: 6,
      }}>
        {cta}
        <ArrowIcon />
      </div>
    </Link>
  );
} */

// New card style for the Signuo tiles

function SignupCard({ priceLabel, title, description, to, cta, accent }) {
  return (
    <Link
      to={to}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "32px 24px",
        background: "#fff",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        borderRadius: 24, // Same smooth 24px rounded corners as OfferTile
        textDecoration: "none",
        color: C.textDark,
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.06)", // Same soft shadow
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.border = "2px solid rgb(8, 131, 62)";
        e.currentTarget.style.boxShadow = "0 0 20px rgba(8, 131, 62, 0.40)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.border = "1px solid rgba(0, 0, 0, 0.08)";
        e.currentTarget.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.06)";
      }}
    >
      {/* Price Pill Badge */}
      {priceLabel && (
        <div
          style={{
            display: "inline-block",
            padding: "6px 16px",
            background:
              accent === C.navy
                ? "#E9EEF6"
                : accent === C.amber
                  ? C.amberLight
                  : C.primaryLight,
            color: accent,
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            marginBottom: 16,
          }}
        >
          {priceLabel}
        </div>
      )}

      {/* Title */}
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: C.navy,
          marginBottom: 10,
        }}
      >
        {title}
      </div>

      {/* Description */}
      <div
        style={{
          fontSize: 14.5,
          color: C.textMed,
          lineHeight: 1.6,
          marginBottom: 24,
          flex: 1,
        }}
      >
        {description}
      </div>

      {/* CTA Button */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 24px",
          background: accent,
          color: "white",
          fontSize: 14,
          fontWeight: 700,
          borderRadius: 999, // Matching rounded pill style
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        {cta}
        <ArrowIcon />
      </div>
    </Link>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

/**function CommunityTile({ img, label, description, accent }) {
  return (
    <div style={{
      background: "#fff",
      border: `1px solid ${C.divider}`,
      borderRadius: 12,
      overflow: "hidden",
      display: "flex", flexDirection: "column",
    }}>
      <div style={{ position: "relative" }}>
        <img
          src={img}
          alt={label}
          loading="lazy"
          style={{ width: "100%", height: 190, objectFit: "cover", display: "block" }}
        />
        <span style={{
          position: "absolute", bottom: -14, left: "50%", transform: "translateX(-50%)",
          display: "inline-flex", alignItems: "center", gap: 6,
          whiteSpace: "nowrap",
          background: accent, color: "#fff",
          padding: "6px 16px", borderRadius: 999,
          fontSize: 13, fontWeight: 700,
          boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
        }}>
          {label}
        </span>
      </div>
      <div style={{
        padding: "28px 20px 20px", textAlign: "center",
        fontSize: 14, color: C.textMed, lineHeight: 1.55, flex: 1,
      }}>
        {description}
      </div>
    </div>
  );
} */

// New card style for community tiles

function CommunityTile({ img, label, description, accent }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        borderRadius: 24, // Same smooth 24px rounded corners
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.06)", // Soft card shadow
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 16px 36px rgba(0, 0, 0, 0.12)";
        e.currentTarget.style.transform = "translateY(-4px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.06)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Top Image Section */}
      <div style={{ position: "relative" }}>
        <img
          src={img}
          alt={label}
          loading="lazy"
          style={{
            width: "100%",
            height: 200,
            objectFit: "cover",
            display: "block",
          }}
        />

        {/* Center Green Badge Pill */}
        <span
          style={{
            position: "absolute",
            bottom: -18,
            left: "50%",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
            background: "rgb(8, 131, 62)", // Primary green
            color: "#fff",
            padding: "8px 20px",
            borderRadius: 999,
            fontSize: 14,
            fontWeight: 700,
            boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
          }}
        >
          {label}
        </span>
      </div>

      {/* Bottom Description Section */}
      <div
        style={{
          padding: "36px 20px 28px",
          textAlign: "center",
          fontSize: 14,
          color: C.textMed,
          lineHeight: 1.55,
          flex: 1,
        }}
      >
        {description}
      </div>
    </div>
  );
}

function StatCard({ count, label, description }) {
  return (
    <div
      style={{
        background: C.navyCard,
        border: `1px solid ${C.navyBorder}`,
        borderRadius: 8,
        padding: "26px 20px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: 34,
          fontWeight: 800,
          color: "#fff",
          letterSpacing: -0.5,
        }}
      >
        {count}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: "#fff",
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: C.onNavyMuted, lineHeight: 1.5 }}>
        {description}
      </div>
    </div>
  );
}
